import type { TurnInstruction } from '../turnByTurn';
import type { YyzTerminal } from './yyzArrivalEntries';

/**
 * YYZ's static/manual walk guide — fixed step-by-step directions from each
 * arrival scope's door to the curbside pickup point, shown WITHOUT needing a
 * live GPS fix. Keyed by terminal first (`T1`/`T3`), then by flight scope —
 * both terminals use their own single pickup door (Door A) for both
 * domestic and international arrivals; only the START of the walk differs.
 *
 * This is a deliberately separate module from the live GPS-tracked walk
 * (`WalkToPickupScreen`'s map mode, `useArrivedAtPickupPoint`/`turnByTurn.ts`
 * driven by the walker's real position): indoor GPS can't always be trusted,
 * and the product owner asked for a manual fallback that works regardless.
 * The two modes are not derived from each other and do not need to agree
 * turn-for-turn — this data is the passenger's own on-site measurement,
 * walked and timed in person, not something computed from the bundled
 * indoor tileset. See `yyzArrivalEntries.ts` for how the T1 anchor points
 * were independently cross-checked against real tileset data, and for the
 * T3 pickup door's own verification against the bundled OSM graph.
 *
 * T1's domestic walk has no turns because the "Canada Arrivals" exit is only
 * a few strides from the curb; T1's international walk is a real interior
 * walk because international arrivals clear customs (Canada Border Services
 * Agency) well back from the curb. T3's domestic walk reuses the same
 * "13m straight" reading — the product owner's own reference note gives one
 * shared domestic line with no explicit terminal heading, so this is
 * carried over as-is rather than guessed at; T3's international walk is two
 * left turns (31m, then 279m to the pickup point), per that same note.
 */
export type YyzStaticWalkStep = {
  instruction: TurnInstruction;
  distanceMeters: number;
  /** Degrees to rotate the guidance arrow — 0 = straight ahead, negative =
   * left, positive = right (matches `angleDelta`'s sign convention). */
  angle: number;
  etaSeconds: number;
  headline: string;
};

type WalkScope = 'Domestic' | 'International';

export const YYZ_STATIC_WALK_ARRIVAL_HEADLINE: Record<YyzTerminal, Record<WalkScope, string>> = {
  T1: {
    Domestic: "You've reached the pickup point",
    International: "Look to your right — you'll see airport officials at the pickup point",
  },
  T3: {
    Domestic: "You've reached the pickup point",
    International: "You've reached the pickup point",
  },
};

export const YYZ_STATIC_WALK_STEPS: Record<YyzTerminal, Record<WalkScope, YyzStaticWalkStep[]>> = {
  T1: {
    Domestic: [
      {
        instruction: 'Continue Straight',
        distanceMeters: 13,
        angle: 0,
        etaSeconds: 10,
        headline: 'Head straight to the pickup point',
      },
    ],
    International: [
      {
        instruction: 'Turn Left',
        distanceMeters: 21.63,
        angle: -90,
        etaSeconds: 15,
        headline: 'Turn left',
      },
      {
        instruction: 'Turn Left',
        distanceMeters: 6.31,
        angle: -90,
        etaSeconds: 5,
        headline: 'Turn left again',
      },
      {
        instruction: 'Continue Straight',
        distanceMeters: 108,
        angle: 0,
        etaSeconds: 60,
        headline: 'Travel straight',
      },
    ],
  },
  T3: {
    Domestic: [
      {
        instruction: 'Continue Straight',
        distanceMeters: 13,
        angle: 0,
        etaSeconds: 10,
        headline: 'Head straight to the pickup point',
      },
    ],
    International: [
      {
        instruction: 'Turn Left',
        distanceMeters: 31,
        angle: -90,
        etaSeconds: 22,
        headline: 'Go left',
      },
      {
        instruction: 'Turn Left',
        distanceMeters: 279,
        angle: -90,
        etaSeconds: 200,
        headline: 'Turn left, then walk to the pickup point',
      },
    ],
  },
};

/** Whole-walk totals, for a summary line above the step list. */
export function yyzStaticWalkTotals(
  terminal: YyzTerminal,
  scope: WalkScope,
): { distanceMeters: number; etaSeconds: number } {
  const steps = YYZ_STATIC_WALK_STEPS[terminal][scope];
  return steps.reduce(
    (totals, step) => ({
      distanceMeters: totals.distanceMeters + step.distanceMeters,
      etaSeconds: totals.etaSeconds + step.etaSeconds,
    }),
    { distanceMeters: 0, etaSeconds: 0 },
  );
}

/**
 * Advances a manual step index, clamping at `steps.length` — that final
 * index is the sentinel "arrived" card (see `WalkToPickupStepsScreen`), one
 * past the last real step. Never goes below 0.
 */
export function clampStaticWalkStepIndex(index: number, stepCount: number): number {
  return Math.max(0, Math.min(index, stepCount));
}

const GENERIC_FALLBACK_STEP: YyzStaticWalkStep = {
  instruction: 'Continue Straight',
  distanceMeters: 0,
  angle: 0,
  etaSeconds: 0,
  headline: 'Head to the pickup point',
};
const GENERIC_FALLBACK_ARRIVAL_HEADLINE = "You've reached the pickup point";

/**
 * The step plan to show on the written-directions screen for a given
 * terminal + flight scope, or a single generic placeholder step when either
 * can't be determined (no flight number, AeroDataBox miss, or a pickup
 * outside both known YYZ terminals) — so the screen still shows something
 * usable rather than nothing, same fallback philosophy as the live map's
 * default behaviour.
 */
export function resolveStaticWalkPlan(
  terminal: YyzTerminal | null,
  scope: WalkScope | null,
): {
  steps: YyzStaticWalkStep[];
  arrivalHeadline: string;
} {
  if (!terminal || !scope) {
    return { steps: [GENERIC_FALLBACK_STEP], arrivalHeadline: GENERIC_FALLBACK_ARRIVAL_HEADLINE };
  }
  return {
    steps: YYZ_STATIC_WALK_STEPS[terminal][scope],
    arrivalHeadline: YYZ_STATIC_WALK_ARRIVAL_HEADLINE[terminal][scope],
  };
}
